import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Box, Button, Flex, Input, Slider, SliderFilledTrack, SliderMark, SliderThumb, SliderTrack, Stack, Text} from "@chakra-ui/react";
import {useMutation} from "@apollo/client";
import {registerSoundFileMutation} from "../../components/soundfiles/soundFileQueries";
import SoundFileLibraryDialog, {ExistingSoundOption} from "../../components/soundfiles/SoundFileLibraryDialog";
import {UploadFileMetadata} from "../../components/upload/Upload";
import {SoundFile, Spot, SpotInfo, Zone} from "../../components/types";
import SpotDetailsAccordion from "../../components/spotList/SpotDetailsAccordion";
import {AmbientZoneCard} from "../areaDetails/zone/AmbientZones";
import {AppContext, AppContextI} from "../../components/context/ContextWrapper";
import {UserContext, UserContextI} from "../../UserContext";
import {useUploadQueueContext} from "../../components/uploadQueue/UploadQueueContext";
import TimeCodeInput from "../../components/form/TimeCodeInput";
import {getCueLabel} from "./cueListHelpers";

// ─── Generic inspectable item ───────────────────────────────────────────────────

/**
 * Adapter that lets the inspector read/write any object uniformly.
 * To support a new object type in the cue list, implement this interface.
 */
export interface InspectableItem {
    /** Unique id for react key / equality checks */
    id: string;
    /** Kind tag for styling and header label */
    kind: string;
    /** Display name of the kind (e.g. "Audio cue", "Ambient") */
    kindLabel: string;
    /** Optional subtitle (e.g. "Cue 003") */
    subtitle?: string;
    /** Accent color scheme for the inspector border/header */
    accentColor: string;
    /** Background color for the inspector panel */
    bgColor: string;

    /** Read the user-facing label */
    getLabel(): string;
    /** Write the label back to the data source */
    setLabel(label: string): void;

    /** Read the primary sound file (if any) */
    getSoundFile(): SoundFile | undefined;
    /** Write sound file identity (fileName, filePath, originalFileName) */
    setSoundFileIdentity(originalFileName: string, fileName: string, filePath: string): void;
    /** Write a single sound file field (delay, fadeIn, fadeOut, volumeDb) */
    setSoundFileField(field: keyof SoundFile, value: number | undefined): void;

    /** Sound file field label override (e.g. "Ambient sound" vs "Sound file") */
    soundFileLabel?: string;
    /** Whether a field has mixed values across a multi-selection. */
    isMixedField?: (field: keyof SoundFile) => boolean;
    /** Whether this represents multiple items (disables per-item fields like name, sound file) */
    isMultiSelect?: boolean;
}

// ─── Spot adapter ───────────────────────────────────────────────────────────────

export const spotToInspectable = (
    spot: Spot,
    cueNumber: number | undefined,
    onUpdate: (spot: Spot, updates: Partial<Spot>) => void,
    getLatest: (id: string) => Spot
): InspectableItem => ({
    id: spot.id,
    kind: "spot",
    kindLabel: "Audio cue",
    subtitle: cueNumber !== undefined ? `Cue ${String(cueNumber).padStart(3, "0")}` : undefined,
    accentColor: "gray",
    bgColor: "white",

    getLabel: () => getCueLabel(getLatest(spot.id)),
    setLabel: (label: string) => {
        const latest = getLatest(spot.id);
        const prefixMatch = /^(\d+\s*-\s*)/.exec(latest.name || "");
        const nextName = prefixMatch ? `${prefixMatch[1]}${label}` : label;
        if (nextName !== latest.name) {
            onUpdate(latest, {name: nextName});
        }
    },

    getSoundFile: () => getLatest(spot.id).soundFiles?.[0],
    setSoundFileIdentity: (originalFileName, fileName, filePath) => {
        const latest = getLatest(spot.id);
        const current = latest.soundFiles?.[0] || {};
        const next: SoundFile = {
            ...current,
            synchronize: (current as SoundFile).synchronize ?? false,
            originalFileName,
            fileName,
            filePath
        };
        onUpdate(latest, {soundFiles: [next]});
    },
    setSoundFileField: (field, value) => {
        const latest = getLatest(spot.id);
        const current = latest.soundFiles?.[0];
        if (!current?.fileName) return;
        const next: SoundFile = {...current, [field]: value};
        onUpdate(latest, {soundFiles: [next]});
    }
});

export const multiSpotToInspectable = (
    spots: Spot[],
    getLatest: (id: string) => Spot,
    onBatchUpdate: (spotsToUpdate: Spot[], buildUpdates: (latestSpot: Spot) => Partial<Spot>) => void
): InspectableItem => {
    const firstSpot = spots[0];
    const getFirstSound = () => getLatest(firstSpot.id).soundFiles?.[0];
    const getNormalizedFieldValue = (spot: Spot, field: keyof SoundFile) => {
        const sound = getLatest(spot.id).soundFiles?.[0];
        if (field === "delay" || field === "fadeIn" || field === "fadeOut" || field === "volumeDb") {
            return sound?.[field] ?? 0;
        }
        return sound?.[field];
    };
    const isMixedField = (field: keyof SoundFile) => {
        if (spots.length <= 1) {
            return false;
        }
        const firstValue = getNormalizedFieldValue(spots[0], field);
        for (let index = 1; index < spots.length; index++) {
            const nextValue = getNormalizedFieldValue(spots[index], field);
            if (nextValue !== firstValue) {
                return true;
            }
        }
        return false;
    };

    return {
        id: `multi-${spots.map((spot) => spot.id).join("-")}`,
        kind: "spot",
        kindLabel: "Audio cues",
        subtitle: `${spots.length} selected`,
        accentColor: "gray",
        bgColor: "white",

        getLabel: () => getCueLabel(getLatest(firstSpot.id)),
        setLabel: (label: string) => {
            onBatchUpdate(spots, (latest) => {
                const prefixMatch = /^(\d+\s*-\s*)/.exec(latest.name || "");
                const nextName = prefixMatch ? `${prefixMatch[1]}${label}` : label;
                if (nextName === latest.name) {
                    return {};
                }
                return {name: nextName};
            });
        },

        getSoundFile: () => getFirstSound(),
        setSoundFileIdentity: (originalFileName, fileName, filePath) => {
            onBatchUpdate(spots, (latest) => {
                const current = latest.soundFiles?.[0] || {};
                const next: SoundFile = {
                    ...current,
                    synchronize: (current as SoundFile).synchronize ?? false,
                    originalFileName,
                    fileName,
                    filePath
                };
                return {soundFiles: [next]};
            });
        },
        setSoundFileField: (field, value) => {
            onBatchUpdate(spots, (latest) => {
                const current = latest.soundFiles?.[0];
                if (!current?.fileName) {
                    return {};
                }
                const next: SoundFile = {...current, [field]: value};
                return {soundFiles: [next]};
            });
        },
        isMixedField,
        isMultiSelect: true
    };
};

// ─── Ambient zone adapter ───────────────────────────────────────────────────────

export const ambientZoneToInspectable = (
    zone: Zone,
    onUpdateZone: (zone: Zone, updates: Partial<Zone>) => void,
    getLatestZone: (id: string) => Zone
): InspectableItem => ({
    id: zone.id,
    kind: "ambient",
    kindLabel: "Ambient",
    accentColor: "purple",
    bgColor: "purple.50",
    soundFileLabel: "Ambient sound",

    getLabel: () => {
        const z = getLatestZone(zone.id);
        return z.label || z.name || "";
    },
    setLabel: (label: string) => {
        const z = getLatestZone(zone.id);
        onUpdateZone(z, {name: label, label});
    },

    getSoundFile: () => getLatestZone(zone.id).soundFiles?.ambient,
    setSoundFileIdentity: (originalFileName, fileName, filePath) => {
        const z = getLatestZone(zone.id);
        const current = z.soundFiles?.ambient || {synchronize: false};
        const updated: SoundFile = {
            ...current,
            synchronize: (current as SoundFile).synchronize ?? false,
            originalFileName,
            fileName,
            filePath
        };
        onUpdateZone(z, {soundFiles: {...z.soundFiles, ambient: updated}});
    },
    setSoundFileField: (field, value) => {
        const z = getLatestZone(zone.id);
        const current = z.soundFiles?.ambient;
        if (!current?.fileName) return;
        const updated: SoundFile = {...current, [field]: value};
        onUpdateZone(z, {soundFiles: {...z.soundFiles, ambient: updated}});
    }
});

// ─── Props ──────────────────────────────────────────────────────────────────────

type Props = {
    spots: Spot[];
    cueNumber?: number;
    ambientZone?: Zone;
    canEdit: boolean;
    existingSoundOptions: ExistingSoundOption[];
    isLibraryLoading: boolean;
    onRequestLibrary: () => void;
    onUpdate: (spot: Spot, updates: Partial<Spot>) => void;
    onUpdateZone: (zone: Zone, updates: Partial<Zone>) => void;
    onEditSpotTriggers?: (spots: Spot[]) => void;
    onEditAmbientTriggers?: (zone: Zone) => void;
};

// ─── Inspector core (generic, works with any InspectableItem) ───────────────────

const InspectorPanel = ({
    item,
    canEdit,
    existingSoundOptions,
    isLibraryLoading,
    onRequestLibrary,
    onEditTriggers
}: {
    item: InspectableItem;
    canEdit: boolean;
    existingSoundOptions: ExistingSoundOption[];
    isLibraryLoading: boolean;
    onRequestLibrary: () => void;
    onEditTriggers?: () => void;
}) => {
    const appContext = useContext<AppContextI>(AppContext);
    const userContext = useContext<UserContextI>(UserContext);
    const {enqueueUploads} = useUploadQueueContext();
    const [registerSoundFile] = useMutation(registerSoundFileMutation);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [selectedExistingFile, setSelectedExistingFile] = useState<string | undefined>(undefined);

    const soundFile = item.getSoundFile();
    const soundFileName = soundFile?.originalFileName || soundFile?.fileName || "";
    const hasSoundFile = !!soundFileName;
    const delayIsMixed = !!item.isMixedField?.("delay");
    const fadeInIsMixed = !!item.isMixedField?.("fadeIn");
    const fadeOutIsMixed = !!item.isMixedField?.("fadeOut");

    const [labelInput, setLabelInput] = useState(item.getLabel());
    const [fadeIn, setFadeIn] = useState<number>(soundFile?.fadeIn ?? 0);
    const [fadeOut, setFadeOut] = useState<number>(soundFile?.fadeOut ?? 0);
    const [delay, setDelay] = useState<number>(soundFile?.delay ?? 0);
    const [pendingVolumeDb, setPendingVolumeDb] = useState(soundFile?.volumeDb ?? 0);

    useEffect(() => {
        setLabelInput(item.getLabel());
        const sf = item.getSoundFile();
        setFadeIn(sf?.fadeIn ?? 0);
        setFadeOut(sf?.fadeOut ?? 0);
        setDelay(sf?.delay ?? 0);
        setPendingVolumeDb(sf?.volumeDb ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on individual fields, not the whole item object
    }, [item.id, item.getLabel, soundFile?.fadeIn, soundFile?.fadeOut, soundFile?.delay, soundFile?.volumeDb,
        soundFile?.originalFileName, soundFile?.fileName]);

    const commitLabel = () => {
        const trimmed = labelInput.trim();
        if (trimmed && trimmed !== item.getLabel()) {
            item.setLabel(trimmed);
        }
    };

    const openSoundDialog = () => {
        onRequestLibrary();
        setSelectedExistingFile(soundFile?.fileName || existingSoundOptions[0]?.sound.fileName);
        setDialogOpen(true);
    };

    useEffect(() => {
        if (!isDialogOpen || selectedExistingFile || existingSoundOptions.length === 0) {
            return;
        }
        setSelectedExistingFile(existingSoundOptions[0].sound.fileName);
    }, [existingSoundOptions, isDialogOpen, selectedExistingFile]);

    useEffect(() => {
        if (!isDialogOpen || !selectedExistingFile || existingSoundOptions.length === 0) {
            return;
        }
        const exists = existingSoundOptions.some((option) => option.sound.fileName === selectedExistingFile);
        if (!exists) {
            setSelectedExistingFile(existingSoundOptions[0].sound.fileName);
        }
    }, [existingSoundOptions, isDialogOpen, selectedExistingFile]);

    const applySoundFileSelection = (originalFileName: string, fileName: string, filePath: string) => {
        item.setSoundFileIdentity(originalFileName, fileName, filePath);
    };

    const handleUpload = (originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => {
        setDialogOpen(false);
        applySoundFileSelection(originalFileName, fileName, filePath);
        const areaId = appContext.getArea()?.id;
        const uploadedBy = userContext.getUser()?.email || userContext.getUser()?.fullName;
        if (areaId) {
            registerSoundFile({
                variables: {areaId, objectId: fileName, originalFileName, filePath, uploadedBy,
                    mimeType: metadata?.mimeType, sizeBytes: metadata?.sizeBytes, durationMs: metadata?.durationMs}
            }).catch(console.error);
        }
    };

    const registerUploadedFile = async (originalFileName: string, fileName: string, filePath: string, metadata?: UploadFileMetadata) => {
        const areaId = appContext.getArea()?.id;
        if (!areaId) {
            return;
        }
        const uploadedBy = userContext.getUser()?.email || userContext.getUser()?.fullName;
        try {
            await registerSoundFile({
                variables: {areaId, objectId: fileName, originalFileName, filePath, uploadedBy,
                    mimeType: metadata?.mimeType, sizeBytes: metadata?.sizeBytes, durationMs: metadata?.durationMs}
            });
        } catch (error) {
            console.warn("Unable to register sound file", error);
        }
        onRequestLibrary();
    };

    const handleAddExisting = () => {
        if (!selectedExistingFile) return;
        const selected = existingSoundOptions.find(opt => opt.sound.fileName === selectedExistingFile);
        if (!selected) return;
        applySoundFileSelection(
            selected.sound.originalFileName || selected.sound.fileName || "",
            selected.sound.fileName || "",
            selected.sound.filePath || "SoundFiles"
        );
        setDialogOpen(false);
    };

    const accentBorder = `${item.accentColor}.200`;
    const accentText = `${item.accentColor}.500`;
    const sliderFill = item.accentColor === "gray" ? "blue.500" : `${item.accentColor}.500`;

    return (
        <Box borderTopWidth={"1px"} borderColor={accentBorder} bg={item.bgColor} px={4} py={3}>
            <Stack spacing={3}>
                <Flex justify={"space-between"} align={"center"}>
                    <Text fontSize={"xs"} color={accentText} textTransform={"uppercase"} letterSpacing={"0.08em"} fontWeight={"bold"}>
                        {item.kindLabel}
                    </Text>
                    {item.subtitle && (
                        <Text fontSize={"xs"} color={"gray.500"}>{item.subtitle}</Text>
                    )}
                </Flex>

                <Flex gap={4} wrap={"wrap"} align={"flex-end"}>
                    <Box minW={"220px"} flex={1}>
                        <Text fontSize={"xs"} fontWeight={"medium"} color={"gray.600"} mb={1}>Name</Text>
                        <Input
                            size={"sm"}
                            value={item.isMultiSelect ? "" : labelInput}
                            placeholder={item.isMultiSelect ? `${item.subtitle || "Multiple selected"}` : undefined}
                            onChange={e => setLabelInput(e.target.value)}
                            onBlur={commitLabel}
                            onKeyDown={e => { if (e.key === "Enter") { commitLabel(); e.currentTarget.blur(); } }}
                            isDisabled={!canEdit || !!item.isMultiSelect}
                        />
                    </Box>

                    <Box minW={"300px"} flex={1}>
                        <Text fontSize={"xs"} fontWeight={"medium"} color={"gray.600"} mb={1}>
                            {item.soundFileLabel || "Sound file"}
                        </Text>
                        <Flex align={"center"} gap={2}>
                            <Text fontSize={"sm"} color={item.isMultiSelect ? "gray.400" : soundFileName ? "gray.700" : "gray.400"} noOfLines={1}>
                                {item.isMultiSelect ? "Multiple cues selected" : soundFileName || "No sound selected"}
                            </Text>
                            {canEdit && !item.isMultiSelect && (
                                <Button size={"xs"} variant={"outline"} onClick={openSoundDialog}>
                                    {soundFileName ? "Replace" : "Pick sound"}
                                </Button>
                            )}
                            {onEditTriggers && (
                                <Button size={"xs"} variant={"outline"} onClick={onEditTriggers} isDisabled={!canEdit}>
                                    Edit triggers
                                </Button>
                            )}
                        </Flex>
                    </Box>
                </Flex>

                <Flex gap={4} wrap={"wrap"} align={"flex-end"}>
                    <Box minW={"120px"}>
                        <Text fontSize={"xs"} fontWeight={"medium"} color={"gray.600"} mb={1}>Delay</Text>
                        <TimeCodeInput
                            valueMs={delay}
                            onChangeMs={(ms) => { setDelay(ms); item.setSoundFileField("delay", ms); }}
                            isDisabled={!canEdit || !hasSoundFile}
                            mixedValue={delayIsMixed}
                        />
                    </Box>
                    <Box minW={"120px"}>
                        <Text fontSize={"xs"} fontWeight={"medium"} color={"gray.600"} mb={1}>Fade in</Text>
                        <TimeCodeInput
                            valueMs={fadeIn}
                            onChangeMs={(ms) => { setFadeIn(ms); item.setSoundFileField("fadeIn", ms); }}
                            isDisabled={!canEdit || !hasSoundFile}
                            mixedValue={fadeInIsMixed}
                        />
                    </Box>
                    <Box minW={"120px"}>
                        <Text fontSize={"xs"} fontWeight={"medium"} color={"gray.600"} mb={1}>Fade out</Text>
                        <TimeCodeInput
                            valueMs={fadeOut}
                            onChangeMs={(ms) => { setFadeOut(ms); item.setSoundFileField("fadeOut", ms); }}
                            isDisabled={!canEdit || !hasSoundFile}
                            mixedValue={fadeOutIsMixed}
                        />
                    </Box>
                    <Box minW={"240px"} flex={1}>
                        <Text fontSize={"xs"} fontWeight={"medium"} color={"gray.600"} mb={2}>Level (dB)</Text>
                        <Box paddingInlineEnd={8} paddingBottom={4}>
                            <Slider
                                aria-label={"Level in dB"}
                                min={-24} max={6} step={1}
                                value={pendingVolumeDb}
                                onChange={setPendingVolumeDb}
                                onChangeEnd={(val) => item.setSoundFileField("volumeDb", val)}
                                isDisabled={!canEdit || !hasSoundFile}
                            >
                                <SliderMark value={-24} mt={3} fontSize={"xs"} color={"gray.600"}>-24</SliderMark>
                                <SliderMark value={0} mt={3} fontSize={"xs"} color={"gray.600"}>0</SliderMark>
                                <SliderMark value={6} mt={3} fontSize={"xs"} color={"gray.600"}>+6</SliderMark>
                                <SliderTrack bg={"gray.200"}>
                                    <SliderFilledTrack bg={sliderFill}/>
                                </SliderTrack>
                                <SliderThumb/>
                            </Slider>
                        </Box>
                    </Box>
                </Flex>
            </Stack>

            <SoundFileLibraryDialog
                isOpen={isDialogOpen}
                onClose={() => setDialogOpen(false)}
                mode={"slot"}
                existingSoundOptions={existingSoundOptions}
                isLoading={isLibraryLoading}
                selectedExistingFile={selectedExistingFile}
                onSelectExistingFile={setSelectedExistingFile}
                onAddExisting={handleAddExisting}
                onUpload={handleUpload}
                registerUploadedFile={registerUploadedFile}
                enqueueUploads={enqueueUploads}
                isReplace={hasSoundFile}
            />
        </Box>
    );
};

// ─── CueListInspector — resolves selection to InspectableItem(s) ────────────────

const CueListInspector = ({
    spots,
    cueNumber,
    ambientZone,
    canEdit,
    existingSoundOptions,
    isLibraryLoading,
    onRequestLibrary,
    onUpdate,
    onUpdateZone,
    onEditSpotTriggers,
    onEditAmbientTriggers
}: Props) => {
    const appContext = useContext<AppContextI>(AppContext);

    const getLatestSpot = useCallback((id: string) => appContext.getSpot(id) || spots.find(s => s.id === id)!, [appContext, spots]);
    const getLatestZone = useCallback((id: string) => {
        const zones = appContext.getArea()?.zones?.ambientZones || [];
        return zones.find(z => z.id === id) || ambientZone!;
    }, [appContext, ambientZone]);

    const applyBatchSpotUpdates = useCallback((spotsToUpdate: Spot[], buildUpdates: (latestSpot: Spot) => Partial<Spot>) => {
        if (!spotsToUpdate || spotsToUpdate.length === 0) {
            return;
        }

        const currentSpots = appContext.getSpots() || [];
        const updatesById = new Map<string, Partial<Spot>>();
        const optimisticById = new Map<string, Spot>();

        spotsToUpdate.forEach((spot) => {
            const latest = appContext.getSpot(spot.id) || spot;
            const updates = buildUpdates(latest);
            if (!updates || Object.keys(updates).length === 0) {
                return;
            }
            updatesById.set(spot.id, updates);
            optimisticById.set(spot.id, {
                ...latest,
                ...updates
            });
        });

        if (optimisticById.size === 0) {
            return;
        }

        appContext.setSpots(currentSpots.map((spot) => optimisticById.get(spot.id) || spot));

        spotsToUpdate.forEach((spot) => {
            const updates = updatesById.get(spot.id);
            if (!updates) {
                return;
            }
            const optimisticSpot = optimisticById.get(spot.id) || appContext.getSpot(spot.id) || spot;
            onUpdate(optimisticSpot, updates);
        });
    }, [appContext, onUpdate]);

    // Resolve the current selection to an inspectable item (single or multi spot selection)
    const item = useMemo<InspectableItem | undefined>(() => {
        if (ambientZone) {
            return ambientZoneToInspectable(ambientZone, onUpdateZone, getLatestZone);
        }
        if (spots.length === 1) {
            return spotToInspectable(spots[0], cueNumber, onUpdate, getLatestSpot);
        }
        if (spots.length > 1) {
            return multiSpotToInspectable(spots, getLatestSpot, applyBatchSpotUpdates);
        }
        return undefined;
    }, [ambientZone, spots, cueNumber, onUpdate, onUpdateZone, applyBatchSpotUpdates, getLatestSpot, getLatestZone]);

    if (!item) {
        const message = spots.length > 1
            ? `${spots.length} cues selected`
            : "Select a cue to edit details.";
        return (
            <Box borderTopWidth={"1px"} borderColor={"gray.200"} bg={"white"} px={4} py={3}>
                <Text fontSize={"sm"} color={"gray.500"}>{message}</Text>
            </Box>
        );
    }

    const allSpots = appContext.getSpots() || [];
    const spotsInfo: SpotInfo[] = allSpots
        .filter((spot): spot is Spot => !!spot && spot.id !== undefined && spot.key !== undefined)
        .map((spot) => ({
            id: spot.id,
            type: spot.type,
            name: spot.name,
            modifiedAt: spot.modifiedAt,
            key: spot.key
        }));

    if (ambientZone) {
        return (
            <Box borderTopWidth={"1px"} borderColor={"gray.200"} bg={"white"} px={4} py={3} overflowY={"auto"}>
                <AmbientZoneCard zone={ambientZone} allSpotsInfo={spotsInfo}/>
            </Box>
        );
    }

    if (spots.length === 1) {
        return (
            <Box borderTopWidth={"1px"} borderColor={"gray.200"} bg={"white"} px={4} py={3} overflowY={"auto"}>
                <SpotDetailsAccordion spot={spots[0]} spotsInfo={spotsInfo}/>
            </Box>
        );
    }

    return (
        <InspectorPanel
            key={item.id}
            item={item}
            canEdit={canEdit}
            existingSoundOptions={existingSoundOptions}
            isLibraryLoading={isLibraryLoading}
            onRequestLibrary={onRequestLibrary}
            onEditTriggers={ambientZone && onEditAmbientTriggers
                ? () => onEditAmbientTriggers(ambientZone)
                : (spots.length > 0 && onEditSpotTriggers ? () => onEditSpotTriggers(spots) : undefined)}
        />
    );
};

export default CueListInspector;
